import { Button } from '@jotta/ui/Button'
import { useThrottle } from '@jotta/hooks'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import { RadixModal } from '@jotta/ui/RadixModal'
import { ToggleButton } from '@jotta/ui/ToggleButton'
import { t } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'

import type {
  ConflictRequest,
  ConflictResponse,
  FileActionType,
  FileConflictType,
} from './FileConflictStore'

type ConflictActions = 'skip' | 'overwrite' | 'rename' | 'cancel' | 'forAll'

interface DialogInfo {
  title: string
  text: string
  actions: ConflictActions[]
}

const title = t`File conflict!`

const DialogInfoObject: {
  [key in FileActionType]: { [key in FileConflictType]: DialogInfo }
} = {
  COPY: {
    FILE_ONTO_FILE: {
      title,
      text: t`A file with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'overwrite', 'forAll'],
    },
    FILE_ONTO_FOLDER: {
      title,
      text: t`A folder with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
    FOLDER_ONTO_FILE: {
      title,
      text: t`A file with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
    FOLDER_ONTO_FOLDER: {
      title,
      text: t`A folder with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
  },
  MOVE: {
    FILE_ONTO_FILE: {
      title,
      text: t`A file with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'overwrite', 'forAll'],
    },
    FILE_ONTO_FOLDER: {
      title,
      text: t`A folder with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
    FOLDER_ONTO_FILE: {
      title,
      text: t`A file with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
    FOLDER_ONTO_FOLDER: {
      title,
      text: t`A folder with the same name already exists`,
      actions: ['cancel', 'skip', 'rename', 'forAll'],
    },
  },
  UPLOAD: {
    FILE_ONTO_FILE: {
      title,
      text: t`A file with the same name is already uploaded.`,
      actions: ['cancel', 'overwrite', 'rename', 'skip', 'forAll'],
    },
    FILE_ONTO_FOLDER: {
      title,
      text: t`A folder with the same name already exists`,
      actions: ['cancel', 'skip', 'forAll'],
    },
    FOLDER_ONTO_FILE: {
      title: '',
      text: '',
      actions: [],
    },
    FOLDER_ONTO_FOLDER: {
      title: '',
      text: '',
      actions: [],
    },
  },
}

function useDialogGrace(ms = 200, initial = false) {
  const [open, _setOpen] = useState(initial)

  const setOpenDelayed = useThrottle(
    useCallback((value: boolean) => {
      _setOpen(value)
    }, []),
    ms,
    { leading: true, trailing: true },
  )

  const setOpen = useCallback(
    (value: boolean) => {
      setOpenDelayed(value)
      if (!value) {
        // Immediately close regardless
        _setOpen(false)
      }
    },
    [setOpenDelayed],
  )

  return [
    open,
    setOpen,
    { cancel: setOpenDelayed.cancel, flush: setOpenDelayed.flush },
  ] as const
}

export const FileConflictDialog = observer<{
  request: ConflictRequest | null
}>(function FileConflictDialog({ request }) {
  const [forAll, setForAll] = useState(false)
  const [open, setOpen, { cancel, flush }] = useDialogGrace()

  const resolve = useCallback(
    (res: ConflictResponse) => {
      request?.resolve(res)
      setOpen(false)
    },
    [request, setOpen],
  )

  useEffect(() => {
    setOpen(Boolean(request))
    setForAll(false)
  }, [request, setOpen])

  // Flush on load
  useEffect(() => {
    flush()
    cancel()
  }, [flush, cancel])

  const resolveAction = useCallback(
    (action: ConflictResponse['action']) => () => resolve({ action, forAll }),
    [resolve, forAll],
  )

  if (!request) {
    return null
  }

  const { filename, actionType, conflictType } = request
  const { title, text, actions } = DialogInfoObject[actionType][conflictType]

  const hasCancel = actions.includes('cancel')
  const hasOverwrite = actions.includes('overwrite')
  const hasRename = actions.includes('rename')
  const hasSkip = actions.includes('skip') && request.hasMultiple
  const hasSkipAll =
    hasSkip && actions.includes('forAll') && !hasOverwrite && !hasRename
  const hasForAll =
    request.hasMultiple && !hasSkipAll && actions.includes('forAll')

  return (
    <RadixModal open={open} onClose={setOpen}>
      <RadixDialog title={title} data-testid="FileConflictDialog">
        <h4
          sx={{
            mt: 0,
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'activeGray',
          }}
        >
          {filename.startsWith('/') ? filename.substring(1) : filename}
        </h4>
        {text && <p>{text}</p>}
        {hasForAll && (
          <div
            sx={{
              display: 'flex',
              mt: 4,
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <ToggleButton
              checked={forAll}
              onCheckedChange={setForAll}
              label={t`Do for all`}
              data-testid="forAll"
            />
          </div>
        )}
        <div
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 4,
            mb: 2,
            mt: 4,
          }}
        >
          <div sx={{ flex: '1 1 auto', display: 'flex' }}>
            {hasCancel || !actions.length ? (
              <Button
                className="btn btn-outline"
                onClick={resolveAction('cancel')}
                data-testid="cancel"
              >
                {!actions.length ? t`Ok` : t`Cancel`}
              </Button>
            ) : null}
          </div>

          {hasSkip && (
            <Button
              sx={{ minWidth: '100px', order: 1 }}
              className="btn btn-outline"
              onClick={resolveAction('skip')}
              data-testid="skip"
            >{t`Skip`}</Button>
          )}
          {hasSkipAll && (
            <Button
              sx={{ minWidth: '100px', order: 1 }}
              className="btn btn-outline"
              onClick={() => resolve({ action: 'skip', forAll: true })}
              data-testid="skip"
            >{t`Skip all`}</Button>
          )}
          {hasOverwrite && (
            <Button
              sx={{ minWidth: '100px', order: 2 }}
              className="btn btn-outline"
              onClick={resolveAction('overwrite')}
              data-testid="overwrite"
            >{t`Overwrite`}</Button>
          )}
          {hasRename && (
            <Button
              sx={{ minWidth: '100px', order: 3 }}
              className="btn btn-outline"
              onClick={resolveAction('rename')}
              data-testid="rename"
            >{t`Keep both`}</Button>
          )}
        </div>
      </RadixDialog>
    </RadixModal>
  )
})
